import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { IExercise } from '../../../../core/models/exercise';
import { IVideo } from '../../../../core/models/video';
import {
  IPracticeEnemFeedback,
  IPracticeEnemFeedbackResolution,
  PracticeEnemDay,
} from '../../model/Enem';
import { getPracticeEnemFeedback } from '../../queries/Enem';
import {
  isDaySelectionRequiredError,
  parseDayParam,
  practiceEnemPaths,
} from '../../utils/enemDay';

export const usePracticeEnemFeedbackHook = () => {
  const [exercises, setExercises] = useState<IExercise[]>([]);
  const [feedbackData, setFeedbackData] = useState({
    wrongAnswersTotal: 0,
    correctAnswersTotal: 0,
    correctAnswersPercentage: 0,
    timeSpent: 0,
  });

  const { slug, day: dayParam } = useParams();
  const day: PracticeEnemDay = parseDayParam(dayParam);

  const navigate = useNavigate();

  const { data, isError, error, isSuccess, isPending } = useQuery({
    queryKey: ['fetch-practice-enem-exam-feedback', slug, day],
    queryFn: () => getPracticeEnemFeedback(slug || '', day),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const breadCrumbsItems = [
    {
      label: 'Simuladão Enem',
      href: practiceEnemPaths.list(),
    },
    ...(day
      ? [
          {
            label: 'Escolha do dia',
            href: practiceEnemPaths.daySelection(slug || ''),
          },
        ]
      : []),
    {
      label: day ? `Gabarito — ${day}º dia` : 'Gabarito',
      href: '',
    },
  ];

  const organizeExercisesFields = (
    resolutions: IPracticeEnemFeedbackResolution[]
  ) => {
    const organizedExercises = resolutions.map((resolution) => {
      const exercise = {} as IExercise;

      if (resolution.alternatives) {
        resolution.alternatives = resolution.alternatives.map((alternative) => {
          if (alternative) {
            alternative.id = alternative._id || alternative.id;

            const hasAlternativeSelected =
              resolution.userAnswerId === alternative.id;

            alternative.selected = hasAlternativeSelected;
            alternative.status =
              hasAlternativeSelected && alternative.correct
                ? 'success'
                : hasAlternativeSelected && !alternative.correct
                  ? 'error'
                  : alternative.correct
                    ? 'correct'
                    : 'default';
          }

          return alternative;
        });
      }

      exercise.id = resolution.questionId;
      exercise.alternatives = resolution.alternatives;
      exercise.exerciseType = 'single_answer';
      exercise.done = true;
      exercise.hasAlternativeSelected = resolution.myAnswer ? true : false;
      exercise.correct = resolution.isCorrect;
      exercise.statement = resolution.statement;
      exercise.resolution_video = {} as IVideo;
      exercise.resolution_video.defaultPlayer = resolution.defaultPlayer;
      exercise.resolution_video.youtubeUrl = resolution.youtubeUrl;
      exercise.resolution_video.sambatech_token = resolution.sambatech_token;
      exercise.resolution_video.vimeoID = resolution.vimeoID;
      exercise.ancestors = resolution.ancestors;
      exercise.order = resolution.order;
      exercise.resolution = resolution.resolution;
      exercise.showAnswer = !!resolution.resolution;

      return exercise;
    });

    return organizedExercises;
  };

  const remakeExam = async () => {
    navigate(practiceEnemPaths.exam(slug || '', day));
  };

  /** Volta para a escolha de dia, para o aluno atacar o dia que falta. */
  const goToDaySelection = () => {
    navigate(practiceEnemPaths.daySelection(slug || ''));
  };

  const handleSuccess = (data: IPracticeEnemFeedback) => {
    const resolutions = data.resolutions;

    setExercises(organizeExercisesFields(resolutions));

    const correctAnswersTotal = getCorrectAnswersTotal(resolutions);

    setFeedbackData({
      correctAnswersPercentage:
        (correctAnswersTotal / resolutions.length) * 100,
      correctAnswersTotal: correctAnswersTotal,
      wrongAnswersTotal: resolutions.length - correctAnswersTotal,
      timeSpent: 0,
    });
  };

  const getCorrectAnswersTotal = (
    resolutions: IPracticeEnemFeedbackResolution[]
  ) => {
    return resolutions.filter((resolution) => resolution.isCorrect).length;
  };

  useEffect(() => {
    if (isSuccess && data) {
      handleSuccess(data);
    }
  }, [data, isSuccess]);

  // Link antigo (/feedback, sem dia) para um simulado que agora tem dois dias:
  // manda o aluno escolher em vez de mostrar "conteúdo não encontrado".
  const needsDaySelection = isDaySelectionRequiredError(error);

  useEffect(() => {
    if (needsDaySelection) {
      navigate(practiceEnemPaths.daySelection(slug || ''), { replace: true });
    }
  }, [needsDaySelection, navigate, slug]);

  return {
    data,
    day,
    isError: isError && !needsDaySelection,
    error,
    isSuccess,
    isLoading: isPending || needsDaySelection,
    exercises,
    wrongAnswersTotal: feedbackData.wrongAnswersTotal,
    correctAnswersTotal: feedbackData.correctAnswersTotal,
    correctAnswersPercentage: feedbackData.correctAnswersPercentage,
    timeSpent: feedbackData.timeSpent,
    breadCrumbsItems,
    hasSecondDay: !!data?.hasSecondDay,
    remakeExam,
    goToDaySelection,
  };
};
